import axios, { AxiosRequestConfig, AxiosError } from "axios"
import config from "~/config";
import httpCode from "~/constants/httpCode";
import { logoutUserOk } from "~/store/actions";
import {
  AccessDeniedException,
  NotFoundException,
  BadRequestException,
  ValidationException,
  AuthException,
  ServerErrorException,
  UnprocessableEntityException,
  ACC_TOKEN_MISSING,
  ACC_TOKEN_JWT_EXPIRED,
  NetworkErrorException,
  REF_TOKEN_NOT_FOUND,
  REF_TOKEN_EXPIRED,
} from "./errorHelper";
import { withAuth } from "./utilHelper";
import type { Store } from "redux";

const axiosApi = axios.create({
  baseURL: (config as any).API_BE_URL,
  withCredentials: true,
});

const refreshEndpoint = withAuth('/refresh');
let isRefreshing = false;
let refreshQueue: Array<{ resolve: () => void; reject: () => void }> = [];
let store: Store | undefined;

axiosApi.interceptors.response.use(
  response => response,
  (error: AxiosError<any>) => {
    if (!!error.response) {
      // see if this error is because the access token expired
      // and therefore we should try to refresh it
      if (shouldRefreshAccessToken(error)) {
        return handleAccessTokenRefresh(error);
      }
      const errorData = error.response.data as any;
      let customError: any;
      switch (error.response.status) {
        case httpCode.unauthorized:
          customError = new AuthException(null, errorData?.code);
          break;
        case httpCode.forbidden:
          customError = new AccessDeniedException(null, errorData?.code);
          break;
        case httpCode.notFound:
          customError = new NotFoundException(null, errorData?.code);
          break;
        case httpCode.badRequest:
          if (errorData?.fields) {
            customError = new ValidationException(null, errorData.fields);
          } else {
            customError = new BadRequestException(null, errorData?.code);
          }
          break;
        case httpCode.unprocessableEntity:
          customError = new UnprocessableEntityException(errorData?.message, errorData?.code);
          break;
        case httpCode.internalServerError:
          customError = new ServerErrorException(null, errorData?.code);
          break;
        default:
          customError = error;
      }
      return Promise.reject(customError);
    } else if (!!error.request) {
      // we have a request but not a response
      // so this is most likely a network problem
      return Promise.reject(new NetworkErrorException());
    }
    return Promise.reject(error);
  }
);

export const get = <T = any>(url: string, config: AxiosRequestConfig = {}): Promise<T> => axiosApi
  .get(url, config)
  .then(response => response.data);

export const post = <T = any>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<T> => axiosApi
  .post(url, data, config)
  .then(response => response.data);

export const put = <T = any>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<T> => axiosApi
  .put(url, data, config)
  .then(response => response.data);

export const del = <T = any>(url: string, config: AxiosRequestConfig = {}): Promise<T> => axiosApi
  .delete(url, config)
  .then(response => response.data);

export const injectStore = (_store: Store) => {
  store = _store
}

export const refreshAccessToken = (previousRequest: any, isRetry = false) => post(refreshEndpoint, {}, { headers: { 'X-Orig-Req': previousRequest } })
  .catch(ex => {
    const isRetryable = ![REF_TOKEN_NOT_FOUND, REF_TOKEN_EXPIRED].includes(ex.response?.data?.code);
    // there are a few scenarios where we know for sure that a retry is pointless
    // for all the rest, we want to try again before deciding that the session is unrecoverable
    if (!isRetry && isRetryable) {
      // wait a brief moment before retrying
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          refreshAccessToken(previousRequest, true)
            .then(resolve)
            .catch(reject);
        }, 100);
      });
    }
    // not retryable
    throw ex;
  });

const shouldRefreshAccessToken = (error: AxiosError<any>) => {
  const originalRequest = error.config;
  if (!originalRequest || !error.response) return false;
  const errorData = error.response.data as any;
  return !!error.response // server error (not client)
    && error.response.status === httpCode.unauthorized // authentication error
    && [ACC_TOKEN_MISSING, ACC_TOKEN_JWT_EXPIRED].includes(errorData?.code) // specific error codes
    && originalRequest.url != refreshEndpoint // not the refresh request itself
    && !(originalRequest as any)._retry; // not a retry request
}

const handleAccessTokenRefresh = (error: AxiosError<any>) => {
  // check if a refresh call is already in progress
  // we must not allow multiple concurrent refresh calls
  // to avoid race conditions
  if (!isRefreshing) {
    // this refresh call is starting so block other refresh calls
    isRefreshing = true;
    // fire the refresh call
    refreshAccessToken(error.config.url)
      // on refresh success
      .then(() => {
        // now that we have a new access token let's retry all queued requests
        refreshQueue.forEach(item => item.resolve());
      })
      // on refresh error
      .catch(() => {
        // we failed to aquire a new access token so let's abort all queued requests
        refreshQueue.forEach(item => item.reject());
        // logout and redirect to login
        if (store) {
          store.dispatch(logoutUserOk());
        }
      })
      .finally(() => {
        // this refresh call is done so allow other refresh calls
        isRefreshing = false;
        // empty the request queue
        refreshQueue = [];
      });
  }
  // multiple concurrent requests might try to refresh the token at the same time
  // we want to do 2 things in respect to this:
  // 1. allow a single refresh call at a time to avoid race conditions (see 'isRefreshing' above)
  // 2. queue all the requests that occur during a refresh call so they can be retried later (see below)
  return new Promise((resolve, reject) => {
    refreshQueue.push({
      // callback to be called after the token is refreshed
      // retries the request
      resolve: () => {
        // get the original request from the error
        const originalRequest = error.config;
        if (!originalRequest) {
          reject(error);
          return;
        }
        // mark the request as being a retry
        // to avoid being retried itself
        (originalRequest as any)._retry = true;
        // fire the retry request
        resolve(axiosApi.request(originalRequest));
      },
      // callback to be called after the token has failed to refresh
      // aborts the request
      reject: () => {
        reject(error);
      },
    })
  });
}